<template>
    <div>
        <div
            class="title d-flex justify-content-between align-items-center mb-3"
        >
            <div class="text mb-0">Export Items</div>
            <div>
                <button
                    class="btn btn-light mr-2"
                    @click="onSync"
                    :disabled="isLoading || isSyncing"
                >
                    <span>Sync Inventory</span>
                </button>
                <button
                    class="btn btn-dark"
                    @click="onExport"
                    :disabled="!selectedItems.length || isLoading"
                >
                    <span
                        >Export
                        {{
                            selectedItems.length > 0
                                ? "(" + selectedItems.length + ")"
                                : ""
                        }}</span
                    >
                </button>
            </div>
        </div>

        <div
            class="d-flex flex-column flex-md-row align-items-start align-items-md-end mb-4"
        >
            <div class="mb-2 mb-md-0 flex-fill">
                <span v-if="selectedItems.length" class="text-muted">
                    {{ selectedItems.length }} item{{
                        selectedItems.length > 1 ? "s" : ""
                    }}
                    selected
                </span>
            </div>
            <div class="col-12 col-md-2 px-0 px-md-2">
                <input
                    type="search"
                    class="form-control"
                    :disabled="isLoading"
                    v-model="searchQuery"
                    placeholder="Search items"
                    key="search-input"
                    ref="searchInput"
                />
            </div>
            <div class="col-12 col-md-2 px-0 px-md-2">
                <select 
                    class="form-control" 
                    v-model="filter.type"
                    :disabled="isLoading"
                >
                    <option :value="null">All Categories</option>
                    <option
                        v-for="category in categories"
                        :key="category.value"
                        :value="category.value"
                    >
                        {{ category.label }}
                    </option>
                </select>
            </div>
            <div class="col-12 col-md-2 px-0 pl-md-2">
                <select 
                    class="form-control"
                    v-model="filter.item_type_category_id"
                    :disabled="isLoading || !filter.type"
                    @change="fetchItems"
                >
                    <option value="">All Subcategories</option>
                    <option
                        v-for="(subcategory, index) in subcategories"
                        :key="index"
                        :value="subcategory.value"
                    >
                        {{ subcategory.label }}
                    </option>
                </select>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table list">
                <thead>
                    <tr>
                        <th class="select-column">
                            <input
                                type="checkbox"
                                class="cursor-pointer"
                                :checked="isAllSelected"
                                @change="toggleSelectAll"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            Item ID
                        </th>
                        <th>
                            <sorting
                                :order-by="filter.sortItem"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'item')
                                "
                                text="Item"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            <sorting
                                :order-by="filter.sortCategory"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'category')
                                "
                                text="Category"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            <sorting
                                :order-by="filter.sortNote"
                                @sorting="
                                    orderBy => sortingData(orderBy, 'note')
                                "
                                text="Notes"
                            />
                        </th>
                        <th class="d-none d-md-table-cell">
                            <sorting
                                :order-by="filter.sortControlled"
                                @sorting="
                                    orderBy =>
                                        sortingData(orderBy, 'controlled')
                                "
                                text="Controlled"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isLoading">
                        <td colspan="6" class="text-center">
                            <loader-list />
                        </td>
                    </tr>
                    <tr v-else-if="!items.length">
                        <td colspan="6" class="text-center">No items found</td>
                    </tr>
                    <template v-else>
                        <tr
                            v-for="item in sortedItems"
                            :key="item.item_id"
                            :class="{
                                'table-active': isItemSelected(item.item_id),
                                'cursor-pointer': true
                            }"
                            @click="onRowClick($event, item)"
                        >
                            <td class="select-column" @click.stop>
                                <input
                                    type="checkbox"
                                    class="cursor-pointer"
                                    :value="item.item_id"
                                    :checked="isItemSelected(item.item_id)"
                                    @change="onInputChange($event, item)"
                                />
                            </td>
                            <td class="d-none d-md-table-cell">
                                {{ maskItemId(item.item_id) }}
                            </td>
                            <td>
                                <item-name
                                    :item="item"
                                    new-tab
                                    @click.native.stop
                                />
                                <div class="d-md-none small text-muted">
                                    {{ item.item_category }} •
                                    {{ item.controlled }}
                                </div>
                            </td>
                            <td class="d-none d-md-table-cell">
                                {{ item.item_category }}
                            </td>
                            <td class="d-none d-md-table-cell">
                                {{ item.note }}
                            </td>
                            <td class="d-none d-md-table-cell">
                                {{ item.controlled }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <scroll-to-top :is-smooth="true" />
        </div>

        <cubex-side-panel
            v-model="showSidePanel"
            :items="allItems"
            :title="`Selected Items`"
            :is-user="false"
            @confirm="confirmExport"
            @update:selected="updateSelectedItems"
        />
    </div>
</template>

<script>
import CubexSidePanel from "@/components/setting-cubex/cubex-side-panel";
import LoaderList from "@/components/elements/loader-list";
import Sorting from "@/module/commons/sorting";
import CubexService from "@/services/setting/cubex";
import { debounce } from "lodash";
import ItemName from "@/components/commons/item-name";
import ScrollToTop from "@/components/commons/scroll-to-top";
/**
 * ExportItems Component
 * A component that manages the display and export functionality of item data in a table format.
 * Features include:
 * - Searchable item list
 * - Sortable columns (ID, Item, Category, Notes)
 * - Bulk selection of items
 * - Export functionality with side panel confirmation
 */
export default {
    name: "ExportItems",
    components: {
        LoaderList,
        Sorting,
        CubexSidePanel,
        ItemName,
        ScrollToTop
    },
    data() {
        return {
            isLoading: false,
            isSyncing: false,
            searchQuery: "",
            items: [],
            allItems: [],
            showSidePanel: false,
            filter: {
                sortId: null,
                sortItem: null,
                sortCategory: null,
                sortNote: null,
                sortControlled: null,
                search: null,
                type: null,
                item_type_category_id: ""
            },
            categories: [],
            subcategories: []
        };
    },
    computed: {
        selectedItems: {
            get() {
                return this.$store.getters["cubex/selectedItems"];
            },
            set(value) {
                this.$store.dispatch("cubex/setSelectedItems", value);
            }
        },
        isAllSelected() {
            return (
                this.items.length > 0 &&
                this.selectedItems.length >= this.items.length
            );
        },
        sortedItems() {
            let sorted = [...this.items];

            if (this.filter.sortId) {
                sorted.sort((a, b) => {
                    const aId = String(a.item_id);
                    const bId = String(b.item_id);

                    return this.filter.sortId === "asc"
                        ? aId.localeCompare(bId)
                        : bId.localeCompare(aId);
                });
            }

            if (this.filter.sortItem) {
                sorted.sort((a, b) => {
                    const aName = String(a.name || "");
                    const bName = String(b.name || "");
                    return this.filter.sortItem === "asc"
                        ? aName.localeCompare(bName)
                        : bName.localeCompare(aName);
                });
            }

            if (this.filter.sortCategory) {
                sorted.sort((a, b) => {
                    const aCat = String(a.item_category || "");
                    const bCat = String(b.item_category || "");
                    return this.filter.sortCategory === "asc"
                        ? aCat.localeCompare(bCat)
                        : bCat.localeCompare(aCat);
                });
            }

            if (this.filter.sortNote) {
                sorted.sort((a, b) => {
                    const aNote = String(a.note || "");
                    const bNote = String(b.note || "");
                    return this.filter.sortNote === "asc"
                        ? aNote.localeCompare(bNote)
                        : bNote.localeCompare(aNote);
                });
            }

            if (this.filter.sortControlled) {
                sorted.sort((a, b) => {
                    const aControlled = String(a.controlled || "");
                    const bControlled = String(b.controlled || "");
                    return this.filter.sortControlled === "asc"
                        ? aControlled.localeCompare(bControlled)
                        : bControlled.localeCompare(aControlled);
                });
            }

            return sorted;
        }
    },
    watch: {
        searchQuery: {
            handler: debounce(function (newValue) {
                this.filter.search = newValue;
                this.fetchItems();
            }, 500),
            immediate: false
        },
        "filter.type": {
            handler(newValue) {
                if (newValue) {
                    this.filter.item_type_category_id = "";
                    this.updateSubcategories(newValue);
                } else {
                    this.filter.item_type_category_id = "";
                    this.subcategories = [];
                }

                this.fetchItems();
            }
        }
    },
    methods: {
        /**
         * Handles the sorting of table data
         * @param {string} orderBy - Sort direction ('asc' or 'desc')
         * @param {string} col - Column to sort by ('id', 'name', 'role', or 'email')
         */
        sortingData(orderBy, col) {
            // Reset all other sorts
            Object.keys(this.filter).forEach(key => {
                if (key.startsWith("sort")) {
                    this.filter[key] = null;
                }
            });

            // Set the new sort
            this.filter[
                `sort${col.charAt(0).toUpperCase() + col.slice(1)}`
            ] = orderBy;
        },

        /**
         * Toggles selection of all items in the table
         */
        toggleSelectAll() {
            if (this.isAllSelected) {
                this.selectedItems = [];
            } else {
                this.items.forEach(item => {
                    this.handleItemSelection(item, true);
                });
            }
        },

        /**
         * Initiates the export process by showing the side panel
         */
        onExport() {
            if (!this.selectedItems.length) return;
            this.showSidePanel = true;
        },

        /**
         * Processes the export of selected items
         * @param {number[]} selectedItemIds - Array of selected item IDs to export
         */
        async confirmExport(selectedItemIds) {
            if (!selectedItemIds.length) return;

            this.isLoading = true;

            try {
                const response = await CubexService.exportItems(
                    selectedItemIds
                );

                if (response?.code === 200 && response?.path) {
                    // download file from given url
                    window.open(response?.path, "_blank");

                    this._message("Items exported successfully");
                    this.selectedItems = [];
                    this.fetchItems();
                } else {
                    this._message(
                        response?.message || "Failed to export items",
                        "danger"
                    );
                }
            } catch (error) {
                console.error("Export error:", error);
                this._message("Failed to export items", "danger");
            } finally {
                this.isLoading = false;
            }
        },

        async fetchCategories() {
            const res = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/category-by-type?with_item_class=1&with_categories=1`
            );

            let resources = res?.data?.response?.resources ?? [];

            this.categories = resources.map(resource => ({
                value: resource.id,
                label: resource.value,
                children: resource.category.map(child => ({
                    value: child.id,
                    label: child.name
                }))
            }));
        },

        /**
         * Fetches items from the API based on search criteria
         */
        async fetchItems() {
            this.isLoading = true;
            const activeElement = document.activeElement;
            const wasSearchFocused = activeElement === this.$refs.searchInput;

            try {
                let params = {};

                params.search = this.filter.search;
                if (this.filter.type) params.type = this.filter.type;
                if (this.filter.item_type_category_id)
                    params.item_type_category_id = this.filter.item_type_category_id;

                const response = await CubexService.getItems(params);

                if (response?.code === 200 && response?.resources) {

                    const mappedItems = response.resources.map(item => ({
                        ...item,
                        id: item.item_id,
                        item_id: item.item_id,
                        name: item.item_description,
                        note: item.item_note,
                        controlled: item.item_control == 1 ? "Yes" : "No",
                        parent_id: item.parent_id,
                        item_description: item.item_description,
                        item_category: item.item_type_category
                            ? `${item.item_category} (${item.item_type_category})`
                            : item.item_category
                    }));

                    if (
                        !this.filter.search &&
                        !this.filter.type &&
                        !this.filter.item_type_category_id
                    ) {
                        // Only update allItems when there's no search filter
                        this.allItems = mappedItems;
                    }
                    
                    // Update subcategories if a type is selected
                    if (this.filter.type) {
                        this.updateSubcategories(this.filter.type);
                    }

                    this.items = mappedItems;
                } else {
                    this._message("Failed to load items", "danger");
                }
            } catch (error) {
                console.error("Fetch error:", error);
                this._message("Failed to load items", "danger");
            } finally {
                this.isLoading = false;

                // Restore focus if it was on the search input
                if (wasSearchFocused) {
                    this.$nextTick(() => {
                        this.$refs.searchInput.focus();
                    });
                }
            }
        },

        /**
         * Masks the item ID for display
         * @param {number} id - The item ID to mask
         * @returns {string} The masked item ID
         */
        maskItemId(id) {
            const idString = String(id);
            const firstElevenDigits = idString.slice(11);
            const maskedPart = "*".repeat(11);
            return `${maskedPart}${firstElevenDigits}`;
        },

        /**
         * Updates the selected items array
         * @param {number[]} newSelected - New array of selected item IDs
         */
        updateSelectedItems(newSelected) {
            this.selectedItems = [...newSelected];
        },

        /**
         * Initiates the sync process
         */
        async onSync() {
            this.isSyncing = true;

            try {
                const response = await CubexService.syncItems(
                    this._setting.business.id
                );

                if (response?.code === 200 && response?.path) {
                    // download file from given url
                    window.open(response?.path, "_blank");

                    this._message("Items synced successfully");
                    this.fetchItems();
                    this.isSyncing = false;
                } else {
                    this._message(
                        response?.message || "Failed to sync items",
                        "danger"
                    );
                    this.isSyncing = false;
                }
            } catch (error) {
                console.error("Sync error:", error);
                this._message("Failed to sync items", "danger");
            }
        },

        isItemSelected(itemId) {
            return this.selectedItems.includes(itemId);
        },

        /**
         * Handles the selection/deselection logic for items and their related items
         * @param {Object} item - The item being toggled
         * @param {boolean} shouldSelect - Whether to select or deselect the items
         */
        handleItemSelection(item, shouldSelect) {
            if (item.parent_id === null || item.parent_id === undefined) {
                // Parent item handling
                const childItems = this.allItems.filter(
                    i => i.parent_id === item.item_id
                );
                const itemsToHandle = [
                    item.item_id,
                    ...childItems.map(child => child.item_id)
                ];

                if (shouldSelect) {
                    this.selectedItems = [
                        ...this.selectedItems,
                        ...itemsToHandle
                    ];
                } else {
                    this.selectedItems = this.selectedItems.filter(
                        id => !itemsToHandle.includes(id)
                    );
                }
            } else {
                // Child item handling
                const siblings = this.allItems.filter(
                    i => i.parent_id === item.parent_id
                );
                const parentItem = this.allItems.find(
                    i => i.item_id === item.parent_id
                );
                const itemsToHandle = [
                    ...siblings.map(sibling => sibling.item_id),
                    parentItem?.item_id
                ].filter(Boolean);

                if (shouldSelect) {
                    this.selectedItems = [
                        ...this.selectedItems,
                        ...itemsToHandle
                    ];
                } else {
                    this.selectedItems = this.selectedItems.filter(
                        id => !itemsToHandle.includes(id)
                    );
                }
            }
        },

        onInputChange(e, item) {
            this.handleItemSelection(item, e.target.checked);
        },

        onRowClick(event, item) {
            // Don't trigger if clicking on a link or other interactive element
            if (
                event.target.tagName.toLowerCase() === "a" ||
                event.target.parentElement.tagName.toLowerCase() === "a"
            ) {
                return;
            }

            const isSelected = this.isItemSelected(item.item_id);
            this.handleItemSelection(item, !isSelected);
        },

        updateSubcategories(categoryId) {
            if (!categoryId) {
                this.subcategories = [];
                return;
            }

            const ct = this.categories.find(
                category => category.value === categoryId
            );

            this.subcategories = ct?.children ?? [];
        }
    },
    mounted() {
        this.fetchItems();
        this.fetchCategories();
    }
};
</script>

<style scoped>
.table th {
    background-color: #f8f9fa;
    white-space: nowrap;
    padding: 0.75rem;
}

.table td,
.table th {
    vertical-align: middle;
}

.table-active {
    background-color: rgba(0, 0, 0, 0.025);
}

.table tr:hover {
    background-color: rgba(0, 0, 0, 0.025);
}

.select-column {
    width: 40px;
}

@media (max-width: 767.98px) {
    .title {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .title .btn {
        width: 100%;
        margin-bottom: 0.5rem;
    }

    .title .btn:last-child {
        margin-bottom: 0;
    }

    .table td {
        padding: 0.5rem;
    }
}
</style>
